import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { ResetPasswordForm } from "../components/auth/ResetPasswordForm";
import logo from "../../assets/logo.png";

interface TokenData {
  access_token: string;
  refresh_token?: string;
  type: string;
}

function parseHashParams(hash: string): TokenData | null {
  if (!hash || hash.length <= 1) return null;

  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get("access_token");
  const type = params.get("type");

  if (!accessToken || type !== "recovery") {
    return null;
  }

  return {
    access_token: accessToken,
    refresh_token: params.get("refresh_token") || undefined,
    type,
  };
}

export function ResetPasswordPage() {
  const location = useLocation();
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Parse token from URL hash
    const data = parseHashParams(location.hash);
    setTokenData(data);
    setIsLoading(false);

    // Clear the hash from URL for security
    if (data && globalThis.history.replaceState) {
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    }
  }, [location.hash]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-800">
        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-800 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <img src={logo} alt="RunLens" className="h-24 mx-auto" />
        </div>

        {tokenData ? (
          <ResetPasswordForm
            accessToken={tokenData.access_token}
            refreshToken={tokenData.refresh_token}
          />
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationCircleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Invalid reset link
            </h2>
            <p className="text-gray-600 mb-6">
              This password reset link is invalid or has expired. Please request
              a new one.
            </p>
            <Link
              to="/forgot-password"
              className="inline-block py-2 px-4 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700"
            >
              Request new link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
